import { findParentScroller } from '@lighthouse/tools'

import type { CollisionArea, FlowLayoutNode, NodeIdWithScope } from '../types'
import { ScrollContainerControl } from '../utils/scroll'
import type { LayoutInteractionInstance, LayoutInteractionPlugin } from './LayoutInteractionEngine'
import {
    DOM_DATA_DISABLED,
    DOM_DATA_LEVELS_NAME,
    DOM_DATA_NO_DRAGGABLE,
    DOM_DATA_NODE_NAME,
    DOM_DATA_NODE_SCOPE,
    DOM_DATA_PARENT_NAME,
    DOM_DATA_REAL_PARENT_ID,
    DOM_DATA_VIRTUAL,
    findNodeById,
    findParentNodeById,
    findTriggerDom,
    getChildrenNodes,
    getIndicatorRect
} from './utils'

const DRAG_MIN_DISTANCE = 5
const INDICATOR_SIZE = 6
const IGNORE_POSITION = new Set(['fixed', 'absolute'])

// 过滤被可见性过滤的元素
function isVisibleInElements(nodes: HTMLElement[]) {
    return (node: FlowLayoutNode) => nodes.some(item => item.getAttribute(DOM_DATA_NODE_NAME) === node.id)
}

const isOverlay = (node: FlowLayoutNode) =>
    node.type === 'overlay' || (node.data?.position?.type && IGNORE_POSITION.has(node.data.position.type))

function getRandomColor(): string {
    const r = Math.floor(Math.random() * 256)
    const g = Math.floor(Math.random() * 256)
    const b = Math.floor(Math.random() * 256)
    return `rgba(${r},${g},${b},0.2)`
}

interface DragEngineConstructorParams {
    onDragEnd: (activeId: NodeIdWithScope | null, over: CollisionArea | null) => void

    collisionAreaDetection: (activeId: NodeIdWithScope | null, over: CollisionArea | undefined) => CollisionArea | null
}

export class DragPlugin implements LayoutInteractionPlugin {
    debugDoms: Element[] = []

    name = 'DRAG_PLUGIN'

    readonly #onDragEnd: DragEngineConstructorParams['onDragEnd']

    readonly #collisionAreaDetection: DragEngineConstructorParams['collisionAreaDetection']

    /** 内存中的碰撞区域 */
    #collisionArea: CollisionArea[] = []

    #overCollisionArea: CollisionArea | null = null

    /** 是否在拖拽节点 */
    #isDragging = false

    #activeId: NodeIdWithScope | null = null

    /** 被抓起的dom */
    #dragNode: HTMLElement | null = null

    /** 克隆的抓起的dom */
    #dragOverlay: HTMLElement | null = null

    /** 指示器 */
    #indicator: HTMLElement | null = null

    #coreInstance: LayoutInteractionInstance | null = null

    #scrollControl: ScrollContainerControl | null = null

    constructor(props: DragEngineConstructorParams) {
        this.#onDragEnd = props.onDragEnd
        this.#collisionAreaDetection = props.collisionAreaDetection
    }

    init(instance: LayoutInteractionInstance) {
        this.#coreInstance = instance
        const scroller = findParentScroller(instance.root)
        if (scroller) {
            this.#scrollControl = new ScrollContainerControl(scroller)
        }

        document.addEventListener('keydown', this.#escKeydownHandler)
    }

    destroy() {
        document.removeEventListener('keydown', this.#escKeydownHandler)
    }

    onNodesRectUpdate = () => {
        // 碰撞区域仅拖拽时使用，非拖拽时滚动不重算，避免卡顿
        if (!this.#isDragging) {
            return
        }
        this.#generateCollisionArea()
    }

    readonly #escKeydownHandler = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            this.cancelDrag()
        }
    }

    handleMousedown = () => {
        const triggerDom = findTriggerDom(this.#coreInstance?.tappedDom, this.#coreInstance?.root)
        if (!triggerDom) {
            this.#activeId = null
            return
        }

        const id = triggerDom.getAttribute(DOM_DATA_NODE_NAME)
        const scope = triggerDom.getAttribute(DOM_DATA_NODE_SCOPE) || undefined
        this.#activeId = id ? { id, scope } : null
    }

    handleMouseMove: LayoutInteractionPlugin['handleMouseMove'] = e => {
        if (!this.#coreInstance || this.#coreInstance.root === this.#coreInstance.tappedDom) {
            return
        }

        if (this.#isDragging) {
            this.#dragMoveHandler(e)
        }

        if (
            this.#activeId &&
            this.#coreInstance.selectedIds.some(item => item.scope === this.#activeId?.scope && item.id === this.#activeId?.id)
        ) {
            return
        }

        const triggerDom = findTriggerDom(this.#coreInstance?.tappedDom, this.#coreInstance?.root)
        if (triggerDom && triggerDom.getAttribute(DOM_DATA_NO_DRAGGABLE)) {
            return
        }

        const isDragAction =
            Math.abs(this.#coreInstance.delta.x) >= DRAG_MIN_DISTANCE || Math.abs(this.#coreInstance.delta.y) >= DRAG_MIN_DISTANCE
        if (isDragAction && !this.#isDragging) {
            this.#dragStartHandler(e)
        }
    }

    handleMouseUp = () => {
        if (this.#isDragging) {
            this.#dragEndHandler()
        }

        this.#activeId = null
    }

    readonly #dragStartHandler = (e: MouseEvent, dragNode?: HTMLElement) => {
        e.stopPropagation()

        document.documentElement.style.setProperty('cursor', 'grabbing')
        document.documentElement.style.setProperty('pointer-events', 'none', 'important')

        const target = e.target as HTMLElement

        this.#isDragging = true

        this.#dragNode = dragNode || findTriggerDom(target, this.#coreInstance?.root) || target

        this.#coreInstance?.collectNodes()

        this.#generateCollisionArea()
    }

    readonly #dragMoveHandler = (e: MouseEvent) => {
        if (!this.#isDragging || !this.#coreInstance) {
            return
        }

        const { data } = this.#coreInstance

        window.getSelection()?.removeAllRanges()
        this.#updateDragOverlay()

        this.#scrollControl?.start(e.clientY)

        const overCollisionArea = this.#collisionArea.filter(item => {
            return e.clientX >= item.x && e.clientX <= item.x + item.width && e.clientY >= item.y && e.clientY <= item.y + item.height
        })

        overCollisionArea.sort((a, b) => {
            const aNode = findNodeById(a.id, a.scope)(data)
            const bNode = findNodeById(b.id, b.scope)(data)
            if (aNode && bNode) {
                if (a.position === 'in' && isOverlay(aNode) && !isOverlay(bNode)) {
                    return -1
                }

                if (b.position === 'in' && isOverlay(bNode) && !isOverlay(aNode)) {
                    return 1
                }
            }

            if (a.position === 'in' && a.level === b.level) {
                return -1
            }
            if (b.position === 'in' && a.level === b.level) {
                return 1
            }

            if (a.level === b.level) {
                return a.width * a.height - b.width * b.height
            }

            return b.level - a.level
        })

        const highestWeightArea = this.#collisionAreaDetection(this.#activeId, overCollisionArea[0])

        this.#overCollisionArea = highestWeightArea
        this.#updateIndicator()
    }

    readonly #dragEndHandler = () => {
        if (!this.#isDragging) {
            return
        }

        this.#onDragEnd(this.#activeId, this.#overCollisionArea)

        this.cancelDrag()
    }

    #generateCollisionArea() {
        if (!this.#coreInstance) {
            return
        }

        this.#collisionArea = []

        const rootRect = this.#coreInstance.root.getBoundingClientRect()
        const nodes = this.#coreInstance.root.querySelectorAll<HTMLElement>(`[${DOM_DATA_NODE_NAME}]:not([${DOM_DATA_DISABLED}])`)

        // 创建碰撞检测区域
        this.#collisionArea.push({
            level: 0,
            id: 'root',
            parentId: 'root',
            position: 'in',
            width: rootRect.width,
            height: rootRect.height,
            x: rootRect.x,
            y: rootRect.y
        })

        const queue = [...nodes]
        while (queue.length > 0) {
            const elm = queue.shift() as HTMLElement

            const id = elm.getAttribute(DOM_DATA_NODE_NAME)
            const scope = elm.getAttribute(DOM_DATA_NODE_SCOPE) || undefined
            const levelStr = elm.getAttribute(DOM_DATA_LEVELS_NAME)
            const parentId = elm.getAttribute(DOM_DATA_PARENT_NAME)
            // const isDisabled = elm.getAttribute(DOM_DATA_DISABLED)
            const isVirtual = elm.getAttribute(DOM_DATA_VIRTUAL)
            const realParentId = elm.getAttribute(DOM_DATA_REAL_PARENT_ID) || undefined

            if (!id || !levelStr || !parentId) {
                continue
            }

            const level = Number(levelStr)

            const nodeRect = this.#coreInstance.nodesMap.get(`${scope || ''}@${id}`)
            if (!nodeRect) {
                continue
            }

            const currentNode = findNodeById(id, scope)(this.#coreInstance.data)
            const positionType = currentNode?.data?.position?.type

            // 如果是基础block并且是定位的，则不参与碰撞区域检测
            if (currentNode?.type === 'block' && positionType && IGNORE_POSITION.has(positionType)) {
                continue
            }

            const parentNode =
                findParentNodeById({ id, scope }, this.#coreInstance.data) ||
                findNodeById(parentId, scope === parentId ? undefined : scope)(this.#coreInstance.data)
            if (!parentNode) {
                continue
            }

            const parentRect = this.#coreInstance.nodesMap.get(`${parentNode.scope || ''}@${parentId}`)
            if (!parentRect) {
                continue
            }

            const { id: parentNodeId, data, scope: parentScope } = parentNode
            const parentContainerId = parentScope ? `${parentScope}@${parentNodeId}` : parentNodeId
            const parentPanelId = this.#coreInstance.getContainerState(parentContainerId)?.currentView
            const children = getChildrenNodes(parentNode, parentPanelId)
            const isVisible = isVisibleInElements([...nodes])
            const filterHiddenChildren = children.filter(isVisible)

            // 自定义视图下的虚拟容器
            // if (isDisabled) {
            //     continue
            // }
            if (isVirtual) {
                this.#collisionArea.push({
                    isVirtual: true,
                    level,
                    id,
                    scope,
                    parentId,
                    position: 'in',
                    width: nodeRect.width,
                    height: nodeRect.height,
                    x: nodeRect.x,
                    y: nodeRect.y
                })
                continue
            }
            const nodeIndex = filterHiddenChildren.findIndex(item => item.id === id)
            // 因为所有面板都出现在了nodeTree中. 所以如果找不到, 则说明node所在的面板被隐藏了, 不参与碰撞区域检测
            if (nodeIndex === -1) {
                continue
            }
            const node = filterHiddenChildren[nodeIndex]
            if (!node) {
                continue
            }

            const isContainerNode = node.type === 'container'

            const isOverlayAndFormContainerNode = node.type === 'overlay' || node.type === 'formContainer'

            // 过滤浮层\定位block的children
            const filterOverlayChildren = filterHiddenChildren.filter(item => !isOverlay(item))

            const filterOverlayNodeIndex = filterOverlayChildren.findIndex(item => item.id === id)
            const isFirstNode = filterOverlayNodeIndex === 0
            const isLastNode = filterOverlayNodeIndex === filterOverlayChildren.length - 1

            const { layout } = data ?? {}

            const parentIsVertical = layout?.align?.direction === 'vertical'

            if (isContainerNode) {
                const containerId = scope ? `${scope}@${id}` : id
                const currentPanelId = this.#coreInstance.getContainerState(containerId)?.currentView
                const children = getChildrenNodes(node, currentPanelId)
                if (!children || children.filter(item => !isOverlay(item) && isVisible(item)).length === 0) {
                    this.#collisionArea.push({
                        level,
                        id,
                        scope,
                        parentId,
                        realParentId,
                        position: 'in',
                        width: nodeRect.width,
                        height: nodeRect.height,
                        x: nodeRect.x,
                        y: nodeRect.y
                    })
                }
            }

            if (
                isOverlayAndFormContainerNode &&
                (!node.children || node.children.filter(item => !isOverlay(item) && isVisible(item)).length === 0)
            ) {
                this.#collisionArea.push({
                    level,
                    id,
                    scope,
                    parentId,
                    realParentId,
                    position: 'in',
                    width: nodeRect.width,
                    height: nodeRect.height,
                    x: nodeRect.x,
                    y: nodeRect.y
                })
            }

            if (isFirstNode && !isOverlay(node)) {
                this.#collisionArea.push({
                    level,
                    id,
                    scope,
                    parentId,
                    realParentId,
                    position: 'before',
                    width: parentIsVertical ? parentRect.width : nodeRect.x - parentRect.x + nodeRect.width / 2,
                    height: parentIsVertical ? nodeRect.y - parentRect.y + nodeRect.height / 2 : parentRect.height,
                    x: parentRect.x,
                    y: parentRect.y
                })
            }

            if (isLastNode && !isOverlay(node)) {
                this.#collisionArea.push({
                    level,
                    id,
                    scope,
                    parentId,
                    realParentId,
                    position: 'after',
                    width: parentIsVertical ? parentRect.width : parentRect.right - nodeRect.right + nodeRect.width / 2,
                    height: parentIsVertical ? parentRect.bottom - nodeRect.bottom + nodeRect.height / 2 : parentRect.height,
                    x: parentIsVertical ? parentRect.x : nodeRect.x + nodeRect.width / 2,
                    y: parentIsVertical ? nodeRect.top + nodeRect.height / 2 : parentRect.y
                })

                continue
            }

            const next = filterOverlayChildren[nodeIndex + 1]
            const nextNode = next && this.#coreInstance.nodesMap.get(`${next.scope || ''}@${next.id}`)
            if (!isOverlay(node) && nextNode) {
                this.#collisionArea.push({
                    level,
                    id,
                    scope,
                    parentId,
                    realParentId,
                    position: 'after',
                    width: parentIsVertical ? parentRect.width : nodeRect.width / 2 + (nextNode.left - nodeRect.right) + nextNode.width / 2,
                    height: parentIsVertical
                        ? nodeRect.height / 2 + (nextNode.top - nodeRect.bottom) + nextNode.height / 2
                        : parentRect.height,
                    x: parentIsVertical ? parentRect.x : nodeRect.x + nodeRect.width / 2,
                    y: parentIsVertical ? nodeRect.top + nodeRect.height / 2 : parentRect.y
                })
            }
        }

        // DEBUG:
        // this.debugDoms.forEach(d => {
        //     d.remove()
        // })
        // this.debugDoms = []
        // this.#collisionArea.forEach((area, i) => {
        //     if (area.id === 'root') {
        //         return
        //     }
        //     const dom = document.createElement('div')
        //     // dom.textContent = area.id
        //     dom.setAttribute('data-id', area.id)
        //     dom.setAttribute('data-position', area.position)
        //     dom.style.width = `${area.width}px`
        //     dom.style.height = `${area.height}px`
        //     dom.style.position = 'absolute'
        //     dom.style.left = '0'
        //     dom.style.top = '0'
        //     dom.style.background = getRandomColor()
        //     dom.style.border = '1px solid red'
        //     dom.style.zIndex = String(i)
        //     dom.style.transform = `translate(${area.x}px, ${area.y}px)`
        //     dom.style.pointerEvents = 'none'
        //     document.body.append(dom)
        //     this.debugDoms.push(dom)
        // })
    }

    #updateDragOverlay() {
        if (!this.#coreInstance) {
            return
        }
        if (!this.#dragNode) {
            return
        }

        if (!this.#dragOverlay) {
            const el = this.#dragNode.cloneNode(true) as HTMLElement
            const rect = this.#dragNode.getBoundingClientRect()
            el.style.pointerEvents = 'none'
            el.style.boxSizing = 'border-box'
            el.style.opacity = '0.6'
            el.style.position = 'absolute'
            el.style.left = `${rect.left}px`
            el.style.top = `${rect.top}px`
            el.style.width = `${rect.width}px`
            el.style.height = `${rect.height}px`
            el.style.willChange = 'transform'
            el.style.boxShadow = 'var(--box-shadow)'
            this.#dragOverlay = el
            // document.body.append(el)
        }

        this.#dragOverlay.style.transform = `translate3d(${this.#coreInstance.delta.x}px, ${this.#coreInstance.delta.y}px, 0)`
    }

    #updateIndicator() {
        if (!this.#coreInstance) {
            return
        }

        if (!this.#overCollisionArea) {
            this.#clearIndicator()
            return
        }

        if (!this.#indicator) {
            const el = document.createElement('div')
            el.style.position = 'absolute'
            el.style.left = '0'
            el.style.top = '0'
            el.style.willChange = 'transform'
            el.style.backgroundColor = 'var(--color-main)'
            el.style.pointerEvents = 'none'
            el.style.zIndex = '2'
            this.#indicator = el

            this.#coreInstance.boundary.append(el)
        }

        const nodeRect = this.#coreInstance.nodesMap.get(`${this.#overCollisionArea.scope || ''}@${this.#overCollisionArea.id}`)
        if (!nodeRect) {
            return
        }

        let parentNode: FlowLayoutNode | undefined
        if (this.#overCollisionArea.id === 'root') {
            parentNode = findNodeById(this.#overCollisionArea.id)(this.#coreInstance.data)
        } else {
            if (this.#overCollisionArea.realParentId) {
                parentNode = findNodeById(this.#overCollisionArea.realParentId, this.#overCollisionArea.scope)(this.#coreInstance.data)
            } else {
                parentNode =
                    findParentNodeById({ id: this.#overCollisionArea.id, scope: this.#overCollisionArea.scope }, this.#coreInstance.data) ||
                    findNodeById(this.#overCollisionArea.parentId, this.#overCollisionArea.scope)(this.#coreInstance.data)
            }
        }

        if (!parentNode) {
            return
        }
        const parentRect = this.#coreInstance.nodesMap.get(`${parentNode.scope || ''}@${this.#overCollisionArea.parentId}`)
        if (!parentRect) {
            return
        }

        const node = findNodeById(
            this.#overCollisionArea.isVirtual ? this.#overCollisionArea.parentId : this.#overCollisionArea.id,
            this.#overCollisionArea.scope
        )(this.#coreInstance.data)
        if (!node) {
            return
        }
        const { id: parentNodeId, data: parentData, scope } = parentNode
        const parentContainerId = scope ? `${scope}@${parentNodeId}` : parentNodeId
        const parentPanelId = this.#coreInstance.getContainerState(parentContainerId)?.currentView
        const children = getChildrenNodes(parentNode, parentPanelId)
        const filterOverlayChildren = children.filter(item => !isOverlay(item))
        const nodeIndex = filterOverlayChildren.findIndex(item => item.id === this.#overCollisionArea?.id)
        const nextNode = filterOverlayChildren[nodeIndex + 1]
        const nextNodeRect = nextNode && this.#coreInstance.nodesMap.get(`${nextNode.scope || ''}@${nextNode.id}`)

        const rect = getIndicatorRect({
            collisionArea: this.#overCollisionArea,
            layout: node.data?.layout,
            parentLayout: parentData?.layout,
            rootRect: this.#coreInstance.boundary.getBoundingClientRect(),
            nodeRect,
            nextNodeRect,
            parentRect,
            indicatorSize: INDICATOR_SIZE
        })
        if (rect) {
            this.#indicator.style.transform = `translate3d(${rect.left /* / this.#coreInstance.scale */}px, ${
                rect.top /* / this.#coreInstance.scale */
            }px, 0)`
            this.#indicator.style.width = `${rect.width /* / this.#coreInstance.scale */}px`
            this.#indicator.style.height = `${rect.height /* / this.#coreInstance.scale */}px`
        }
    }

    #clearCollisionArea() {
        this.#collisionArea = []
        this.#overCollisionArea = null
    }

    #clearDragOverlay() {
        document.documentElement.style.removeProperty('cursor')
        document.documentElement.style.removeProperty('pointer-events')

        this.#dragOverlay?.remove()
        this.#dragOverlay = null
    }

    #clearIndicator() {
        this.#indicator?.remove()
        this.#indicator = null
    }

    startDrag(e: MouseEvent, activeId?: NodeIdWithScope, dragNode?: HTMLElement) {
        if (!this.#coreInstance) {
            return
        }

        this.#coreInstance.isActive = true
        if (dragNode) {
            this.#coreInstance.tappedDom = dragNode
        }

        this.#coreInstance.initialCoordinate = {
            x: e.clientX,
            y: e.clientY
        }

        if (activeId) {
            this.#activeId = activeId
        }

        this.#dragStartHandler(e, dragNode)
    }

    cancelDrag() {
        this.#scrollControl?.stop()

        if (this.#coreInstance) {
            this.#coreInstance.isActive = false
            this.#coreInstance.initialCoordinate = { x: 0, y: 0 }
            this.#coreInstance.delta = { x: 0, y: 0 }
        }
        this.#activeId = null
        this.#dragNode = null
        this.#isDragging = false

        this.#clearCollisionArea()
        this.#clearIndicator()
        this.#clearDragOverlay()
    }
}
